import React, { useEffect, useRef, useState } from "react";
import { Box, makeStyles, useTheme, SvgIcon, Container, Typography } from "@material-ui/core";
import { connect, ConnectedProps } from "react-redux";
import { withRouter, RouteComponentProps } from "react-router";
import { ApplicationState, remoteActions, CHECKOUT_ROUTE, DONE_ROUTE } from "../../store";
import DistanceSelector from "../../components/DistanceSelector";
import AddOns from "../../components/AddOns";
import InsuranceCoverageSelect from "../../components/InsuranceCoverageSelect";
import NavToButton from "../../components/Buttons/NavToButton";
import CompositionBox from "../../components/CompositionBox";
import Footer from "../../components/Footer";
import DamageFreeYearOptions from "../../components/DamageFreeYearOptions";
import DynamicDivider from "../../components/DynamicDivider";
import YoungDriverOptions from "../../components/YoungDriverOptions";
import { MessageBox } from "../../components/MessageBox";
import { DealerPriceBottomMessage, DealerPriceTopMessage } from "../../components/DealerPriceMessages";
import USPMessageBox from "../../components/USPMessageBox";
import { TextButton } from "../../components/Buttons";
import { ArrowForward } from "@material-ui/icons";
import clsx from "clsx";
import FirstPageSkeleton from "../../components/FirstPageSkeleton";
import { Theme } from "../../.themes";
import focusAndScroll from "../../helpers/refs/focusAndScroll";
import WebshopDataDeliveryService from "../../services/WebshopDataDeliveryService";
import USPBlock from "../../components/USPBlock";
import Header from "../../components/Header";
import useHotjar from "../../hooks/useHotjar";
//import useVwo from "../../hooks/useVwo";
import { shouldShowStickyPriceButton } from "../../helpers/shouldShowStickyPriceButton";
import { resetServerError } from "../../store/error";
import WelcomeSection from "../../components/WelcomeSection/WelcomeSection";
import StickyPriceButton from "../../components/StickyPriceButton";

const useStyles = makeStyles((theme: Theme) => ({
  header: {
    backgroundColor: theme.palette.backgroundSecondary?.default,
    textAlign: "center",
    padding: `${theme.spacing(3)}px 0`,
    marginBottom: "calc(-2rem - 1px)", // funny hack to fix big spacing for volvia, and merge divider line with background for other brands
    "& hr": {
      border: "none",
      width: "75px",
      backgroundColor: "transparent",
      margin: 0,
    },
    ...theme.header,
  },
  batteryElectricMessage: {
    color: theme.batteryElectricTextBox?.textColor ?? theme.palette?.primary?.main,
    backgroundColor: theme.batteryElectricTextBox?.backgroundColor ?? theme?.palette?.info.light,
    fontWeight: theme.mileageInfoBox?.fontWeight ?? 400,
    padding: 24,
    fontSize: "1rem",
    lineHeight: "24px",
  },
  backButton: {
    minHeight: 0,
    padding: 0,
    ...theme.CheckoutPage?.backButton,
  },
  divider: {
    margin: "2rem auto",
    ...theme.divider,
  },
  sticky: {
    margin: "2rem auto",
  },
  arrowForward: {
    transform: "rotate(180deg)",
  },
  layout: {
    backgroundColor: theme?.appBackgroundColor ?? "#fff",
  },
  stickyLayout: {
    paddingBottom: "100px",
  },
  uspContainerBlockWrapper: {
    display: "flex",
    justifyContent: "center",
    flexDirection: "column",
    margin: "32px 0px 32px 0px",
    width: "100%",
    maxWidth: "100%",
    paddingTop: "39px",
    minHeight: "176px",
    [theme.breakpoints.up("md")]: {
      flexDirection: "row",
      margin: "51px 0px 38px 0px",
      paddingTop: "29px",
      paddingBottom: "32px",
    },
    ...theme.uspContainerBlockWrapper,
  },
  stickyPriceButton: {
    fontFamily: theme.typography.fontFamily,
  },
}));

const SHOW_EARLY_PRICE = false;

class App extends React.PureComponent<AppProps> {
  public componentDidMount() {
    if (this.props.ui.completed) {
      this.props.history.push(DONE_ROUTE);
    }

    if (this.props.ui.initialized) {
      return;
    }

    this.props.requestInitialData();
  }

  public render() {
    return <AppWebshop {...this.props} />;
  }
}

export function AppWebshop(props: AppProps) {
  const damageFreeYearOptionsRef = useRef<HTMLDivElement>(null);
  const youngDriverRef = useRef<HTMLDivElement>(null);
  const deductibleRef = useRef<HTMLDivElement>(null);
  const InsuranceCoverageRef = useRef<HTMLDivElement>(null);
  const [uspBlocks, setUspBlocks] = useState<any[]>([]);

  // Always call useHotjar, passing boolean to enable only for volviaforetag
  useHotjar(props.inputs?.brand?.toLowerCase() === "volviaforetag");
  //useVwo(props.inputs?.partner);
  const showStickyPriceButton =
    props.ui.initialized && !props.dealerPrice?.IsDealerPrice && shouldShowStickyPriceButton(props.inputs?.partner);

  const navToCheckout = () => {
    const selectedCoverageCode = props?.insurance?.coverage?.selectedCode;
    const selectedCoverage = props.insurance?.coverage?.Coverages?.find(coverage => coverage.InternalCode === selectedCoverageCode);
    if (props.damageFreeYear?.questionVisible && props.damageFreeYear?.selected === null) {
      props.validateDamageFree();
      focusAndScroll(damageFreeYearOptionsRef);
    } else if (props.youngDriver?.questionVisible && props.youngDriver?.selected === null) {
      props.validateYoungDriver();
      focusAndScroll(youngDriverRef);
    } else if (props.inputs.webshopMode === 2 && selectedCoverage?.Deductibles?.length && !props.insurance?.coverage?.SelectedDeductible) {
      props.validateSelfRisk();
      focusAndScroll(deductibleRef);
    } else {
      props.history.push(CHECKOUT_ROUTE);
    }
  };
  const theme = useTheme() as Theme;
  const classes = useStyles(theme);
  type IsRullaUt = true | false | null | undefined;
  const isRullaUt: IsRullaUt = props.insurance?.startDate.isDisabled;

  useEffect(() => {
    FetchUspBlockData(props.inputs.cmsHost, props.inputs.webshopPageGuid).then(result => {
      if (!result) return;
      setUspBlocks(result.uspBlocks);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const icon = (theme.icons?.backIcon && (
    <SvgIcon fontSize='large' color='primary' className={clsx(classes.arrowForward)}>
      {theme.icons?.backIcon}
    </SvgIcon>
  )) ?? <ArrowForward fontSize='large' color='primary' className={clsx("arrowForward")} />;

  const header = props.inputs.decoupledRendering?.isDecoupledRendering ? <Header /> : null;

  if (!props.ui.initialized) {
    return (
      <>
        {header}
        <FirstPageSkeleton />
      </>
    );
  }
  localStorage.setItem("Webshop Page", "App");

  return (
    <div className={clsx(classes.layout, showStickyPriceButton && classes.stickyLayout)}>
      {header}
      <Box component='div' display='flex1' flexDirection='row'>
        <div className={classes.header}>
          <CompositionBox maxWidth={832}>
            <WelcomeSection
              carImageUrl={props.profile?.carImageUrl}
              carModel={props.profile?.carModel}
              carRegistrationNumber={props.inputs.crn.toUpperCase()}
              welcomeHeaderPartOneText={props.texts?.welcomeHeaderPartOneText}
            />
          </CompositionBox>
        </div>

        {!showStickyPriceButton && <Footer showYearlyPrice={SHOW_EARLY_PRICE} />}

        {showStickyPriceButton && (
          <div id='sticky_price_button_container'>
            <StickyPriceButton
              onClick={navToCheckout}
              id='go_to_checkout_sticky_button'
              priceState={props.footer}
              isLoading={props.ui.processing}
              buttonClassName={clsx(classes.stickyPriceButton, "checkout_button")}
              showYearlyPrice={SHOW_EARLY_PRICE}
            />

            <DynamicDivider maxWidth={1200} className={classes.divider} />
            <CompositionBox maxWidth={theme.containerMaxWidth ?? 412}>
              <Typography variant='h2'>Anpassa din försäkring</Typography>
            </CompositionBox>
          </div>
        )}
        {props.usp?.IsEnabled && (
          <React.Fragment>
            <div className={classes.divider} />
            <CompositionBox maxWidth={theme.containerMaxWidth ?? 412}>
              <USPMessageBox />
            </CompositionBox>
          </React.Fragment>
        )}
        {props.dealerPrice?.IsDealerPrice && (
          <React.Fragment>
            <div className={classes.divider} />
            <CompositionBox maxWidth={theme.containerMaxWidth ?? 412}>
              <DealerPriceTopMessage />
            </CompositionBox>
          </React.Fragment>
        )}
        {props.texts?.antiTheftMessage && (
          <React.Fragment>
            <div className={classes.divider} />
            <CompositionBox maxWidth={theme.containerMaxWidth ?? 412}>
              <MessageBox>{props.texts?.antiTheftMessage}</MessageBox>
            </CompositionBox>
          </React.Fragment>
        )}
        {isRullaUt && !props.inputs.disableBuying && props.texts?.infoBlockText && (
          <React.Fragment>
            <div className={classes.divider} />
            <CompositionBox maxWidth={theme.containerMaxWidth ?? 412}>
              <MessageBox>{props.texts?.infoBlockText}</MessageBox>
            </CompositionBox>
          </React.Fragment>
        )}
        {props.inputs.disableBuying && props.texts?.kamuxMessage && (
          <React.Fragment>
            <div className={classes.divider} />
            <CompositionBox maxWidth={theme.containerMaxWidth ?? 412}>
              <MessageBox>{props.texts?.kamuxMessage}</MessageBox>
            </CompositionBox>
          </React.Fragment>
        )}
        {props.profile?.isCarVolvoBatteryElectric && props.texts?.batteryElectricMessage && (
          <CompositionBox maxWidth={theme.containerMaxWidth ?? 412}>
            <Typography className={classes.batteryElectricMessage}>{props.texts.batteryElectricMessage}</Typography>
          </CompositionBox>
        )}
        <div className={classes.divider} />
        <CompositionBox maxWidth={theme.containerMaxWidth ?? 412}>
          <DistanceSelector />
        </CompositionBox>
        {props.youngDriver?.questionVisible && (
          <React.Fragment>
            <CompositionBox maxWidth={theme.containerMaxWidth ?? 412}>
              <YoungDriverOptions forwardedRef={youngDriverRef} />
            </CompositionBox>
          </React.Fragment>
        )}
        {props.damageFreeYear?.questionVisible && (
          <React.Fragment>
            <CompositionBox maxWidth={theme.containerMaxWidth ?? 412} marginTop={theme.spacing(2)}>
              <DamageFreeYearOptions forwardedRef={damageFreeYearOptionsRef} />
            </CompositionBox>
          </React.Fragment>
        )}
        <DynamicDivider maxWidth={1200} className={classes.divider} />
        <CompositionBox>
          <InsuranceCoverageSelect
            showSecondaryPrice={Boolean(isRullaUt)}
            forwardedRef={InsuranceCoverageRef}
            deductibleRef={deductibleRef}
          />
        </CompositionBox>
        {props.insurance?.coverage?.IsSingleRecommened && props.insurance.coverage.isCarUnder3YearsAndNotImported && (
          <div>
            <DynamicDivider maxWidth={1200} className={classes.divider} />
            <CompositionBox maxWidth={theme.containerMaxWidth ?? 412}>
              <div style={{ textAlign: "center" }}>
                <TextButton
                  id='singleRecommended'
                  onClick={() => {
                    props.singleRecommendedCoverage();
                    if (InsuranceCoverageRef.current) {
                      InsuranceCoverageRef.current.scrollIntoView({
                        behavior: "smooth",
                        block: "center",
                      });
                      InsuranceCoverageRef.current.focus({ preventScroll: true });
                    }
                  }}
                  value='false'
                  color='primary'
                  endIcon={icon}
                  className={classes.backButton}
                >
                  {props.texts?.singleRecommendedButton}
                </TextButton>
              </div>
            </CompositionBox>
          </div>
        )}
        {!props.dealerPrice?.IsDealerPrice && (
          <React.Fragment>
            <DynamicDivider maxWidth={1200} className={classes.divider} />
            <CompositionBox maxWidth={1200}>
              <AddOns />
            </CompositionBox>
          </React.Fragment>
        )}
        {uspBlocks.length > 0 && (
          <>
            <Container id='usp_container' className={classes.uspContainerBlockWrapper}>
              {uspBlocks.map((uspBlock, index) => (
                <USPBlock key={index} {...uspBlock}></USPBlock>
              ))}
            </Container>
          </>
        )}
        {uspBlocks.length === 0 && <DynamicDivider maxWidth={1200} className={classes.divider} />}
        <CompositionBox maxWidth={theme.containerMaxWidth ?? 412}>
          {props.dealerPrice?.IsDealerPrice ? (
            <DealerPriceBottomMessage />
          ) : (
            !showStickyPriceButton &&
            !props.inputs.disableBuying && (
              <NavToButton onClick={navToCheckout} id='go_to_checkout' className='checkout_button'>
                {props.texts?.checkoutButtonText ?? "Fortsätt"}
              </NavToButton>
            )
          )}
        </CompositionBox>
      </Box>
    </div>
  );
}

export async function FetchUspBlockData(cmsHost: string, webshopPageGuid: string) {
  return await WebshopDataDeliveryService(cmsHost, "getUspBlocks", webshopPageGuid);
}

const connector = connect((state: ApplicationState) => state, {
  requestInitialData: remoteActions.requestInitialData,
  validateDamageFree: remoteActions.validateDamageFree,
  validateYoungDriver: remoteActions.validateYoungDriver,
  validateSelfRisk: remoteActions.validateSelfRisk,
  singleRecommendedCoverage: remoteActions.singleRecommendedCoverage,
  resetServerError,
});

type AppProps = ConnectedProps<typeof connector> & RouteComponentProps;

export default withRouter(connector(App));
